import {Component, inject} from '@angular/core';
import {AutoUnsubscribe} from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ActivatedRoute} from '@angular/router';
import {DatePipe} from '@angular/common';
import {GridEventOutput, GridModel} from 'src/app/shared/model/page-grid';
import {ClientStatementGridService} from '../../service/client-statement-grid.service';
import {AdxInvoiceDetailsModalComponent} from 'src/app/adx-invoice/components/adx-invoice-details-modal/adx-invoice-details-modal.component';
import {AdxBookingDetailsModalComponent} from 'src/app/adx-booking/components/adx-booking-details-modal/adx-booking-details-modal.component';

@Component({
    selector: 'client-statement',
    templateUrl: './client-statement.component.html',
    standalone: false
})

@AutoUnsubscribe()
export class ClientStatementComponent {
  private dialogRef: MatDialogRef<ClientStatementComponent> | null = inject(MatDialogRef<ClientStatementComponent>, {optional: true});
  protected data = inject<any>(MAT_DIALOG_DATA, {optional: true});
  private dialog = inject(MatDialog);

  route = inject(ActivatedRoute);
  datePipe = inject(DatePipe);

  clientId?:string;
  clientName?: string;
  asOfDate?: string;

  gridName?: string;
  noData = false;

  protected readonly clientStatementGrid: GridModel<any> = {
      gridDataTestId: 'client-statement-list',
      gridTitle: 'Client Statement',
      replayButton: false,
      pdfButton: true,
      singleColumnFilter: true,
      gridTitleFunction: (filter:any)=> `${this.clientName} Client Statement as of ${this.datePipe.transform(filter.asOfDate, 'dd MMMM yyyy')}`,
      defaultSort: { active: 'invoiceIssueDate', direction: 'desc' },
      columns: [
        {
          columnProperty: 'adxInvoiceId',
          columnHeader: 'Fms Invoice Number',
          sort: true,
          clickable: true,
        },
        {
          columnProperty: 'adxBookingId',
          columnHeader: 'Fms Service Number',
          sort: true,
          clickable: true,
        },
        {
          columnProperty: 'invoiceIssueDate',
          columnHeader: 'Invoice Issue Date',
          processData: (statement)=> this.datePipe.transform(statement, 'M/dd/yyyy') || '',
          sort: true,
        },
        {
          columnProperty: 'adxBookingStartDate',
          columnHeader: 'Service Start Date',
          processData: (statement)=> this.datePipe.transform(statement, 'M/dd/yyyy') || '',
          sort: true,
        },
        {
          columnProperty: 'adxBookingVendorName',
          columnHeader: 'Vendor Name',
          textLeft: true,
          sort: true,
        },
        {
          columnProperty: 'adxBookingTotalFare',
          columnHeader: 'Total Fare',
          sort: true,
        },
        {
          columnProperty: 'adxBookingTotalTaxes',
          columnHeader: 'Total Taxes',
          sort: true,
        },
        {
          columnProperty: 'accountBalance',
          columnHeader: 'Account Balance',
          sort: true,
        },
        {
          columnProperty: 'adxBookingRemarks',
          columnHeader: 'Remarks',
          sort: true,
        },
      ],
      filters: [
        {
          filterProperty: 'asOfDate',
          filterDisplayName: 'As Of Date',
          filterType: 'DATE',
        }
      ],
      gridGetService: inject(ClientStatementGridService),
  };

  constructor() {
    if(this.data) {
      this.clientId = `${this.data.clientId}`;
      this.clientName = this.data.clientName;
      this.asOfDate = this.data.asOfDate;
    } else {
      this.clientId = this.route.snapshot.paramMap.get('clientId') ?? '';
      this.clientName = this.route.snapshot.queryParamMap.get('ClientName') ?? '';
      this.asOfDate = this.route.snapshot.queryParamMap.get('AsOfDate') ?? '';

    }

    if(this.clientId && this.clientName && this.asOfDate) {
        this.gridName = `${this.clientName} Client Statement as of ${this.datePipe.transform(this.asOfDate, 'dd MMMM yyyy')}`;
    } else {
      this.noData = true;
    }
  }

  ngOnInit(): void {
    if(this.gridName) {
      this.clientStatementGrid.gridTitle = this.gridName;
      this.clientStatementGrid.filtersInitial = {
        clientId: this.clientId ?? '',
        asOfDate: new Date(this.asOfDate!) ?? '',
      };
    }
  }

  closeModal(): void {
    if(this.dialogRef) {
      this.dialogRef.close();
    }
  }
  
  protected openDialog({item, column}: GridEventOutput<any>): void {
    if(column === 'adxInvoiceId') {
      this.handleInvoiceIdClick(item);
    }
    if(column === 'adxBookingId') {
      this.handleServiceIdClick(item);
    }
  }

  private handleInvoiceIdClick(element: any): void {
    this.dialog.open(AdxInvoiceDetailsModalComponent, {
      width: '55%',
      data: { adxInvoiceId: element.adxInvoiceId, title: 'Invoice information' }
    });
  }

  private handleServiceIdClick(element: any): void {
    this.dialog.open(AdxBookingDetailsModalComponent, {
      width: '55%',
      data: { adxBookingId: element.adxBookingId, title: 'Service information' }
    });
  }
}